import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import { CardLogo, CardTypes } from "@industriousoffice/react-components";
import CardValidator from "card-validator";
import { ChangeEvent, useMemo, useId, useCallback } from "react";
import { formatCardNumber } from "../../../utils/formatCardNumber";

interface IFormCardNumberInputProps {
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  disabled?: boolean;
  isProtected?: boolean;
  cardType?: CardTypes;
  "data-testid"?: string;
}

export default function FormCardNumberInput<
  FormValues extends FieldValues = FieldValues
>({
  label,
  placeholder,
  isRequired,
  isDisabled,
  disabled,
  isProtected,
  cardType: cardTypeProp,
  "data-testid": testId,
  ...controllerProps
}: UseControllerProps<FormValues> & IFormCardNumberInputProps) {
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController(controllerProps);

  const inputId = useId();

  const detectedCardType = useMemo<CardTypes | undefined>(() => {
    if (cardTypeProp) return cardTypeProp;
    const result = CardValidator.number(field.value?.replace(/\s/g, ""));
    if (result.isPotentiallyValid && result.card?.type) {
      return result.card.type as CardTypes;
    }
    return undefined;
  }, [field.value, cardTypeProp]);

  const isFieldDisabled = isSubmitting || isDisabled || disabled;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isProtected) return;
      field.onChange(formatCardNumber(e.target.value));
    },
    [isProtected, field]
  );

  const displayValue = useMemo(
    () => (isProtected ? field.value : formatCardNumber(field.value ?? "")),
    [field.value, isProtected]
  );

  return (
    <span className="payment-card-number-field inline-flex flex-col w-full relative">
      {label && (
        <label
          htmlFor={inputId}
          className={`inline-block pb-1 text-xs ${
            error ? "payment-error" : ""
          }`}
        >
          {label}
          {isRequired ? "*" : ""}
        </label>
      )}
      <span className="relative inline-flex w-full">
        <input
          {...field}
          id={inputId}
          aria-invalid={error != null}
          aria-required={isRequired}
          aria-describedby={error == null ? undefined : `${inputId}-error`}
          value={displayValue}
          onChange={handleChange}
          disabled={isFieldDisabled}
          placeholder={placeholder ?? "1234 1234 1234 1234"}
          inputMode="numeric"
          autoComplete="cc-number"
          maxLength={19}
          data-testid={testId}
          className={[
            "bg-white border px-3 py-2 rounded outline-0 min-h-10 w-full pr-16",
            isFieldDisabled ? "text-grey-40" : "",
            error ? "payment-invalid-border" : "border-grey-60",
          ]
            .filter(Boolean)
            .join(" ")}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
          {detectedCardType ? (
            <CardLogo cardType={detectedCardType} />
          ) : (
            <span className="hidden sm:flex gap-1">
              {(
                [
                  CardTypes.Visa,
                  CardTypes.Mastercard,
                  CardTypes.Amex,
                  CardTypes.Discover,
                ] as CardTypes[]
              ).map((t) => (
                <CardLogo key={t} cardType={t} className="mr-1" />
              ))}
            </span>
          )}
        </span>
      </span>
      <div
        id={`${inputId}-error`}
        hidden={error == null}
        role={error == null ? undefined : "alert"}
        className={`pt-2 text-sm ${error ? "payment-error" : "text-grey-25"}`}
      >
        {error?.message ?? <span>&nbsp;</span>}
      </div>
    </span>
  );
}
